import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Collapse,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import {
  loadTciHappyPathProgress,
  setTciHappyPathChecklistHidden,
} from "services/fde/tciHappyPathService";

/**
 * Floating TCI happy-path checklist for org admins (FacilityChecklist pattern).
 */
const TciHappyPathChecklist = () => {
  const { user, userData, organizationId, isGlobalAdmin } = useAuth();
  const { canAccess } = useOrgAdminAccess();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    userData?.organizationId ||
    organizationId ||
    null;

  const [expanded, setExpanded] = useState(true);
  const [showChecklist, setShowChecklist] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(null);

  const isAdminViewer = canAccess || isGlobalAdmin;

  useEffect(() => {
    const saved = localStorage.getItem("tciHappyPathChecklistExpanded");
    if (saved !== null) setExpanded(saved === "true");
  }, []);

  useEffect(() => {
    if (!user?.uid || !isAdminViewer) {
      setShowChecklist(false);
      setIsLoaded(true);
      return;
    }
    if (userData?.hideTciHappyPathChecklist) {
      setShowChecklist(false);
      setIsLoaded(true);
      return;
    }
    setShowChecklist(true);
    setIsLoaded(true);
  }, [user?.uid, isAdminViewer, userData?.hideTciHappyPathChecklist]);

  const refresh = useCallback(async () => {
    if (!institutionId || !showChecklist) return;
    try {
      const next = await loadTciHappyPathProgress(institutionId);
      setProgress(next);
      if (next.allComplete) {
        setShowChecklist(false);
      }
    } catch (err) {
      console.warn("[TciHappyPathChecklist] load failed", err);
    }
  }, [institutionId, showChecklist]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    localStorage.setItem("tciHappyPathChecklistExpanded", String(next));
  };

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setShowChecklist(false);
      setTciHappyPathChecklistHidden(true).catch((err) => {
        console.error("Error saving TCI checklist preference:", err);
      });
    }, 200);
  };

  if (!isLoaded || !showChecklist || !progress || progress.allComplete) {
    return null;
  }

  const percent = Math.round(
    (progress.completedCount / Math.max(progress.totalCount, 1)) * 100,
  );

  const positionSx = isMobile
    ? {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        maxWidth: "100%",
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
      }
    : {
        position: "fixed",
        right: 20,
        bottom: 20,
        maxWidth: 360,
      };

  return (
    <Fade in={!isExiting}>
      <Paper
        variant="elevation"
        elevation={4}
        sx={{
          ...positionSx,
          overflow: "hidden",
          zIndex: 1200,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            borderBottom: expanded ? 1 : 0,
            borderColor: "divider",
          }}
          onClick={toggleExpand}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AssignmentIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              TCI setup
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              aria-label="Dismiss TCI setup checklist"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              sx={{ mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            {expanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {progress.completedCount} of {progress.totalCount} complete
              {progress.nextStep ? ` · Next: ${progress.nextStep.title}` : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{ mb: 1.5, height: 6, borderRadius: 1 }}
            />
            <List dense disablePadding>
              {progress.steps.map((step) => (
                <ListItem
                  key={step.id}
                  component={RouterLink}
                  to={step.done ? "/dashboard/tci-setup" : step.href}
                  sx={{
                    color: "inherit",
                    textDecoration: "none",
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {step.done ? (
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={step.title}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
            <Typography
              component={RouterLink}
              to="/dashboard/tci-setup"
              variant="body2"
              color="primary"
              sx={{ textDecoration: "none", fontWeight: 500 }}
            >
              Open full setup page
            </Typography>
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  );
};

export default TciHappyPathChecklist;
